import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Target, Plus, Trash2, ArrowLeft, ArrowRight, Check, Sparkles, Loader2, GripVertical, MessageCircle, ChevronRight } from 'lucide-react';
import { apiClient } from '../api/client';

const SKILL_OPTIONS = [
  'social_skills', 'confidence', 'empathy', 'introductions', 'small_talk',
  'active_listening', 'conflict_handling', 'leadership', 'teamwork',
  'public_speaking', 'negotiation', 'networking', 'de_escalation', 'feedback',
];

const STEP_HINTS = {
  home: ['Say hi to someone you don\'t normally talk to', 'Start a conversation with a neighbour', 'Call a friend you haven\'t spoken to in a while', 'Express gratitude to a family member'],
  school: ['Ask a question in class', 'Introduce yourself to someone new at lunch', 'Join a group activity or club', 'Give a classmate a genuine compliment'],
  work: ['Ask one clarifying question in your next meeting', 'Give positive feedback to a colleague', 'Introduce yourself to someone from another team', 'Volunteer for a presentation or demo'],
};

// ─── CREATE WIZARD ──────────────────────────────────────────────────────────

const PathwayCreate = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [skillArea, setSkillArea] = useState('');
  const [context, setContext] = useState('home');
  const [steps, setSteps] = useState([{ label: '', instructions: '' }]);
  const [saving, setSaving] = useState(false);
  const [pathwayId, setPathwayId] = useState(null);

  const addStep = () => { if (steps.length < 5) setSteps([...steps, { label: '', instructions: '' }]); };
  const removeStep = (i) => { if (steps.length > 1) setSteps(steps.filter((_, idx) => idx !== i)); };
  const updateStep = (i, field, val) => setSteps(steps.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  const canProceedStep1 = title.trim() && skillArea;
  const canProceedStep2 = steps.length >= 3 && steps.every(s => s.label.trim());

  const handleCreate = async () => {
    setSaving(true);
    try {
      const pw = await apiClient.request('/api/pathways', {
        method: 'POST', body: JSON.stringify({ title, description, skill_area: skillArea, context })
      });
      const id = pw.pathway_id;
      setPathwayId(id);

      for (const s of steps) {
        await apiClient.request(`/api/pathways/${id}/steps`, {
          method: 'POST', body: JSON.stringify({ label: s.label, instructions: s.instructions || undefined })
        });
      }

      await apiClient.request(`/api/pathways/${id}/activate`, { method: 'POST' });
      navigate(`/pathways/${id}`);
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const hints = STEP_HINTS[context] || STEP_HINTS.home;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-2xl mx-auto" data-testid="pathway-create">
        <button onClick={() => navigate('/pathways')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft size={16} /> Back to Pathways
        </button>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? 'w-16 bg-purple-500' : 'w-10 bg-gray-200'}`} />
          ))}
        </div>

        {/* Step 1 — Focus */}
        {step === 1 && (
          <div data-testid="wizard-step-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">What&apos;s your focus?</h2>
            <p className="text-sm text-gray-500 mb-6">Pick one area you&apos;d like to feel a bit stronger in over the next couple of weeks.</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Pathway Name *</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Speak up once a day" className="w-full px-4 py-3 border border-gray-200 rounded-xl mb-4" />

            <label className="block text-sm font-medium text-gray-700 mb-1">What&apos;s this about? (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="A short description for yourself" className="w-full px-4 py-3 border border-gray-200 rounded-xl h-20 resize-none mb-4" />

            <label className="block text-sm font-medium text-gray-700 mb-1">Skill Focus *</label>
            <select value={skillArea} onChange={e => setSkillArea(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl mb-4 bg-white capitalize">
              <option value="">Choose a skill...</option>
              {SKILL_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-2">Context</label>
            <div className="flex gap-3 mb-6">
              {['home', 'school', 'work'].map(c => (
                <button key={c} onClick={() => setContext(c)}
                  className={`flex-1 py-3 rounded-xl font-medium text-sm capitalize transition-all border-2 ${context === c ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200'}`}>{c}</button>
              ))}
            </div>

            <button onClick={() => setStep(2)} disabled={!canProceedStep1}
              className="w-full py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 disabled:opacity-40 flex items-center justify-center gap-2">
              Next <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Step 2 — Steps */}
        {step === 2 && (
          <div data-testid="wizard-step-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">What will you do?</h2>
            <p className="text-sm text-gray-500 mb-6">Add 3-5 small real-world actions. Keep them specific and achievable.</p>

            <div className="space-y-3 mb-4">
              {steps.map((s, i) => (
                <div key={i} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                    <input value={s.label} onChange={e => updateStep(i, 'label', e.target.value)}
                      placeholder={hints[i] || 'What will you do?'} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                    {steps.length > 1 && (
                      <button onClick={() => removeStep(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                    )}
                  </div>
                  <input value={s.instructions} onChange={e => updateStep(i, 'instructions', e.target.value)}
                    placeholder="Any notes for yourself? (optional)" className="w-full px-3 py-2 border border-gray-100 rounded-lg text-xs text-gray-500" />
                </div>
              ))}
            </div>

            {steps.length < 5 && (
              <button onClick={addStep} className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-500 rounded-xl hover:border-purple-300 hover:text-purple-600 text-sm flex items-center justify-center gap-1 mb-4">
                <Plus size={14} /> Add Step
              </button>
            )}

            {hints.length > 0 && (
              <div className="p-3 bg-purple-50 rounded-xl mb-6">
                <p className="text-xs font-bold text-purple-700 mb-1">Need ideas?</p>
                {hints.slice(0, 3).map((h, i) => <p key={i} className="text-xs text-purple-600">- {h}</p>)}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl">Back</button>
              <button onClick={() => setStep(3)} disabled={!canProceedStep2}
                className="flex-1 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 disabled:opacity-40 flex items-center justify-center gap-2">
                Next <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Review */}
        {step === 3 && (
          <div data-testid="wizard-step-3">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Review & Start</h2>
            <p className="text-sm text-gray-500 mb-6">Most people spread a pathway over 1-2 weeks. Your AvA will help you talk through each step.</p>

            <div className="p-4 bg-gray-50 rounded-xl mb-4">
              <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
              {description && <p className="text-sm text-gray-600 mb-2">{description}</p>}
              <div className="flex gap-2 mb-3">
                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full capitalize">{skillArea.replace(/_/g, ' ')}</span>
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full capitalize">{context}</span>
              </div>
              <ol className="space-y-2">
                {steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="w-5 h-5 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                    <span className="text-gray-700">{s.label}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl">Back</button>
              <button onClick={handleCreate} disabled={saving}
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Start Pathway
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ─── PATHWAY DETAIL ─────────────────────────────────────────────────────────

const PathwayDetail = () => {
  const { pathwayId } = useParams();
  const navigate = useNavigate();
  const [pathway, setPathway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [reflection, setReflection] = useState('');

  // useCallback Guard — pinned to `pathwayId` so the mount / route-change
  // effect below can list it as a dep without triggering an infinite
  // reload loop.
  const load = useCallback(() => {
    apiClient.request(`/api/pathways/${pathwayId}`)
      .then(d => setPathway(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pathwayId]);

  useEffect(() => { load(); }, [load]);

  const completeStep = async (stepId) => {
    setCompleting(stepId);
    try {
      const result = await apiClient.request(`/api/pathways/${pathwayId}/steps/${stepId}/complete`, { method: 'POST' });
      if (result.status === 'pathway_completed') {
        setShowCelebration(true);
      }
      load();
    } catch (e) {}
    setCompleting(null);
  };

  const submitReflection = async () => {
    await apiClient.request(`/api/pathways/${pathwayId}/reflection`, {
      method: 'POST', body: JSON.stringify({ response_text: reflection })
    });
    setShowCelebration(false);
    load();
  };

  if (loading) return <DashboardLayout><div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-500" size={24} /></div></DashboardLayout>;
  if (!pathway) return <DashboardLayout><p className="p-8 text-gray-500">Pathway not found</p></DashboardLayout>;

  const steps = pathway.steps || [];
  const done = steps.filter(s => s.completed_at).length;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-2xl mx-auto" data-testid="pathway-detail">
        <button onClick={() => navigate('/pathways')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{pathway.title}</h1>
            {pathway.description && <p className="text-sm text-gray-600 mt-1">{pathway.description}</p>}
            <div className="flex gap-2 mt-2">
              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full capitalize">{pathway.skill_area?.replace(/_/g, ' ')}</span>
              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full capitalize">{pathway.context}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${pathway.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>{pathway.status}</span>
            </div>
          </div>
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${pathway.status === 'completed' ? 'bg-gradient-to-r from-amber-400 to-orange-500' : 'bg-gray-200'}`}>
            <Target size={24} className={pathway.status === 'completed' ? 'text-white' : 'text-gray-400'} />
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600">{done} of {steps.length} steps</span>
            <span className="text-sm font-medium text-gray-900">{Math.round((done / Math.max(steps.length, 1)) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all" style={{ width: `${(done / Math.max(steps.length, 1)) * 100}%` }} />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3 mb-6">
          {steps.map((s, i) => (
            <div key={s.step_id} className={`p-4 rounded-xl border-2 transition-colors ${s.completed_at ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${s.completed_at ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {s.completed_at ? <Check size={16} /> : <span className="text-sm font-bold">{i + 1}</span>}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${s.completed_at ? 'text-green-800 line-through' : 'text-gray-900'}`}>{s.label}</p>
                  {s.instructions && <p className="text-xs text-gray-500 mt-1">{s.instructions}</p>}
                  {s.completed_at && <p className="text-xs text-green-600 mt-1">Completed {new Date(s.completed_at).toLocaleDateString()}</p>}
                </div>
                {!s.completed_at && pathway.status === 'active' && (
                  <button onClick={() => completeStep(s.step_id)} disabled={completing === s.step_id}
                    className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50">
                    {completing === s.step_id ? <Loader2 size={12} className="animate-spin" /> : 'Mark Done'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Ava hint */}
        {pathway.status === 'active' && (
          <div className="p-3 bg-purple-50 rounded-xl flex items-start gap-2 mb-4">
            <MessageCircle size={16} className="text-purple-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-purple-700">Your AvA can help you rehearse any of these steps. Try starting a session and saying: &quot;Help me prepare for my pathway step about...&quot;</p>
          </div>
        )}

        {/* Reflection (if completed) */}
        {pathway.reflection && (
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
            <p className="text-xs font-bold text-amber-700 mb-1">Your Reflection</p>
            <p className="text-sm text-amber-900 italic">&quot;{pathway.reflection.response_text}&quot;</p>
          </div>
        )}
      </div>

      {/* Celebration Modal */}
      {showCelebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Pathway Complete!</h2>
            <p className="text-gray-600 mb-4">You completed all steps in &quot;{pathway.title}&quot;. Badge unlocked!</p>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-4">
              <Target size={32} className="text-white" />
            </div>
            <p className="text-sm text-gray-700 mb-2 font-medium">What&apos;s one thing you learned about yourself?</p>
            <textarea value={reflection} onChange={e => setReflection(e.target.value)}
              placeholder="Optional but encouraged..." className="w-full p-3 border border-gray-200 rounded-xl h-20 resize-none text-sm mb-4" />
            <div className="flex gap-2">
              <button onClick={submitReflection} className="flex-1 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700">Save & Close</button>
              <button onClick={() => setShowCelebration(false)} className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl">Skip for Now</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// ─── PATHWAY LIST ───────────────────────────────────────────────────────────

const PathwayList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('active');

  useEffect(() => {
    apiClient.request('/api/pathways/mine').then(setData).catch(() => {});
  }, []);

  const owned = data?.owned || [];
  const filtered = owned.filter(p =>
    tab === 'active' ? p.status === 'active' :
    tab === 'completed' ? p.status === 'completed' :
    p.status === 'draft'
  );

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Badge Pathways</h1>
          <button onClick={() => navigate('/pathways/create')}
            className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 flex items-center gap-1">
            <Plus size={14} /> Create New
          </button>
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6">
          {['active', 'completed', 'drafts'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-md text-sm font-medium capitalize ${tab === t ? 'bg-white shadow-sm text-purple-700' : 'text-gray-500'}`}>{t}</button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <Target size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 mb-1">No {tab} pathways yet</p>
            {tab === 'active' && <p className="text-xs text-gray-400">Create one to get started</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(p => {
              const done = p.progress?.completed || 0;
              const total = p.progress?.total || 1;
              return (
                <div key={p.pathway_id} onClick={() => navigate(`/pathways/${p.pathway_id}`)}
                  className="p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow cursor-pointer flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${p.status === 'completed' ? 'bg-gradient-to-r from-amber-400 to-orange-500' : 'bg-purple-100'}`}>
                    <Target size={20} className={p.status === 'completed' ? 'text-white' : 'text-purple-600'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{p.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500 capitalize">{p.skill_area?.replace(/_/g, ' ')}</span>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs text-gray-500 capitalize">{p.context}</span>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs text-gray-500">{done}/{total} steps</span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-gray-400" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export { PathwayCreate, PathwayDetail, PathwayList };
